import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import SharingScope from '../components/common/SharingScope';
import { getChapter, getChapters, createMemory } from '../services/chaptersAPI';

/**
 * AddChapterMemory - Add a memory to a specific chapter
 *
 * Memories are lighter than milestones - title and year are optional.
 * Can be accessed with or without a chapterId - auto-assigns chapter based on year.
 */
function AddChapterMemory() {
  const { chapterId } = useParams();
  const navigate = useNavigate();

  const [chapters, setChapters] = useState([]);
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    year: '',
    text: '',
    selectedChapterId: chapterId || '',
  });
  const [sharingScope, setSharingScope] = useState('single_family');
  const [sharingFamilyIds, setSharingFamilyIds] = useState([]);

  // Load chapters data
  useEffect(() => {
    async function loadData() {
      try {
        const chaptersData = await getChapters();
        setChapters(chaptersData);

        if (chapterId) {
          const data = await getChapter(chapterId);
          setChapter(data);
          setFormData(prev => ({ ...prev, selectedChapterId: chapterId }));
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Could not load chapters');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [chapterId]);

  // Auto-assign chapter based on selected year
  useEffect(() => {
    if (chapters.length > 0 && formData.year && !chapterId) {
      const year = parseInt(formData.year);
      const matchingChapter = findChapterForYear(year, chapters);
      if (matchingChapter) {
        setChapter(matchingChapter);
        setFormData(prev => ({ ...prev, selectedChapterId: matchingChapter.id }));
      }
    }
  }, [formData.year, chapters, chapterId]);

  const findChapterForYear = (year, chaptersList) => {
    const currentYear = new Date().getFullYear();
    const sorted = [...chaptersList].sort((a, b) => b.start_year - a.start_year);

    for (const ch of sorted) {
      const endYear = ch.end_year || currentYear;
      if (year >= ch.start_year && year <= endYear) {
        return ch;
      }
    }

    // Fallback: find closest
    return chaptersList.reduce((closest, ch) => {
      const end = ch.end_year || currentYear;
      const dist = Math.min(Math.abs(year - ch.start_year), Math.abs(year - end));
      const closestDist = closest
        ? Math.min(Math.abs(year - closest.start_year), Math.abs(year - (closest.end_year || currentYear)))
        : Infinity;
      return dist < closestDist ? ch : closest;
    }, null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // At least some content is required
    if (!formData.text.trim() && !formData.title.trim()) {
      setError('Please add a title or story text');
      return;
    }

    // Need a chapter to save to
    if (!formData.selectedChapterId) {
      setError('Please select a year so we can assign this to a chapter');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await createMemory({
        chapter: formData.selectedChapterId,
        title: formData.title || '',
        year: formData.year ? parseInt(formData.year) : null,
        text: formData.text,
        sharing_scope: sharingScope,
        family_ids: sharingFamilyIds,
      });

      // Navigate back to chapter view or main page
      navigate(`/my-life/chapters/${formData.selectedChapterId}`);
    } catch (err) {
      console.error('Error creating memory:', err);
      setError('Could not save memory. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Generate year options
  const currentYear = new Date().getFullYear();
  let startYear, endYear;

  if (chapter) {
    startYear = chapter.start_year;
    endYear = chapter.end_year || currentYear;
  } else if (chapters.length > 0) {
    // Use range from all chapters
    startYear = Math.min(...chapters.map(c => c.start_year));
    endYear = Math.max(...chapters.map(c => c.end_year || currentYear));
  } else {
    startYear = 1920;
    endYear = currentYear;
  }

  const years = [];
  for (let y = endYear; y >= startYear; y--) {
    years.push(y);
  }

  const handleBack = () => {
    if (chapterId) {
      navigate(`/my-life/chapters/${chapterId}`);
    } else {
      navigate('/my-life');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fb-olive"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-fb-cream py-6 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="text-fb-olive hover:underline mb-4 flex items-center gap-2"
            >
              <span>&larr;</span> {chapterId ? 'Back to Chapter' : 'Back to My Life'}
            </button>
            <h1 className="font-serif text-2xl font-bold text-gray-900">
              Add a Memory
            </h1>
            {chapter && (
              <p className="text-gray-600 mt-1">
                to "{chapter.title}" ({chapter.start_year}–{chapter.end_year || 'Present'})
              </p>
            )}
            <p className="text-sm text-gray-500 mt-2">
              Memories are smaller moments - no title or date required.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6">
            {/* Title (optional) */}
            <div className="mb-6">
              <label className="block font-semibold text-gray-800 mb-2">
                Title (optional)
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Sunday dinners at Grandma's"
                className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-lg focus:border-fb-olive focus:outline-none"
              />
            </div>

            {/* Year (optional) */}
            <div className="mb-6">
              <label className="block font-semibold text-gray-800 mb-2">
                Year (optional)
              </label>
              <select
                name="year"
                value={formData.year}
                onChange={handleChange}
                className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-lg focus:border-fb-olive focus:outline-none"
              >
                <option value="">I don't remember exactly</option>
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {/* Auto-assigned Chapter (when not coming from a specific chapter) */}
            {!chapterId && chapter && formData.year && (
              <div className="mb-6 bg-fb-warm rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <span>📖</span>
                  <span>This belongs in:</span>
                </div>
                <h3 className="font-serif font-bold text-gray-900">
                  Chapter {chapter.number}: "{chapter.title}"
                </h3>
                <p className="text-sm text-gray-600">
                  {chapter.start_year} – {chapter.end_year || 'Present'}
                </p>
              </div>
            )}

            {/* Story Text */}
            <div className="mb-6">
              <label className="block font-semibold text-gray-800 mb-2">
                What do you remember?
              </label>
              <textarea
                name="text"
                value={formData.text}
                onChange={handleChange}
                rows={6}
                placeholder="Share a memory, a feeling, a small moment..."
                className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-lg focus:border-fb-olive focus:outline-none resize-none"
              />
            </div>

            {/* Voice Note Hint */}
            <div className="bg-fb-warm rounded-lg p-4 mb-6 flex items-center gap-3">
              <span className="text-2xl">🎙</span>
              <div>
                <p className="text-sm text-gray-700">
                  <strong>Prefer to talk?</strong> Voice recording coming soon!
                </p>
              </div>
            </div>

            {/* Sharing Scope */}
            <SharingScope
              value={sharingScope}
              onChange={(scope, familyIds) => {
                setSharingScope(scope);
                setSharingFamilyIds(familyIds);
              }}
              className="mt-4 mb-4"
            />

            {/* Submit */}
            <button
              type="submit"
              disabled={saving}
              className="w-full bg-fb-olive hover:bg-fb-olive/90 text-white font-bold text-lg py-4 rounded-xl disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Memory'}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
}

export default AddChapterMemory;
